import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useCallback, useState } from "react";
import { Box, Typography, Switch, FormControlLabel, IconButton, } from "@mui/material";
import CommonTable from "../../common_components/CommonTable";
import TableToolbar from "../../common_components/TableToolbar";
import { formatStat } from "../../helper/statsFormatter";
import { StyledCurrency } from "../../helper/StyledCurrency";
import { fetchPreferences, savePreferences, selectPreferences, updateNetworkPreference } from "../../redux/divergenceSlice";
import { useDispatch, useSelector } from "react-redux";
import HoverTooltip from "../../common_components/hoverOverlay/HoverTooltip";
import { ExportButton } from "../../common_components/FormInputs";
import { exportEmailChannelExcel } from "./emailChannelExporter";
import CampaignPreviewDrawer from "./CampaignPreviewDrawer";
const getHiddenColumnsFromPreferences = (preferences, networkKey) => {
    const hiddenSet = new Set();
    if (!preferences?.[networkKey])
        return hiddenSet;
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        if (config?.visibility === false) {
            hiddenSet.add(metricKey);
        }
    });
    return hiddenSet;
};
const getColumnOrderFromPreferences = (preferences, networkKey, defaultMetrics) => {
    if (!preferences?.[networkKey] || preferences[networkKey].length === 0) {
        return defaultMetrics;
    }
    const orderedMetrics = [];
    const preferencesKeys = [];
    preferences[networkKey].forEach(item => {
        const metricKey = Object.keys(item)[0];
        const config = item[metricKey];
        preferencesKeys.push(metricKey);
        if (config?.visibility !== false) {
            orderedMetrics.push({ key: metricKey, order: config?.order ?? 999 });
        }
    });
    orderedMetrics.sort((a, b) => a.order - b.order);
    const sortedKeys = orderedMetrics.map(m => m.key);
    const remainingMetrics = defaultMetrics.filter(m => !preferencesKeys.includes(m));
    return [...sortedKeys, ...remainingMetrics];
};
const calendarDateToString = (dateObj) => {
    if (!dateObj)
        return '';
    if (typeof dateObj === 'string')
        return dateObj;
    if (dateObj.year && dateObj.month && dateObj.day) {
        const y = dateObj.year;
        const m = String(dateObj.month).padStart(2, '0');
        const d = String(dateObj.day).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }
    if (typeof dateObj.toString === 'function') {
        const str = dateObj.toString();
        if (str && !str.includes('[object')) {
            return str;
        }
    }
    if (dateObj.start) {
        return calendarDateToString(dateObj.start);
    }
    console.warn('Unable to convert date object:', dateObj);
    return '';
};
const NAME_KEYS = ["campaign_name", "flow_name", "sms_name", "name"];
const EmailChannelTable = ({ channelKey, title, tableData, currentDateRange, previousDateRange, clientId }) => {
    const dispatch = useDispatch();
    const preferences = useSelector(selectPreferences);
    const [columnVisibilityAnchor, setColumnVisibilityAnchor] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [showPrevData, setShowPrevData] = useState(false);
    const showPreviewColumn = channelKey !== "flows";
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedRowForPreview, setSelectedRowForPreview] = useState(null);
    useEffect(() => {
        dispatch(fetchPreferences());
    }, [dispatch]);
    const networkKey = `klaviyo_${channelKey}`;
    const [sortConfig, setSortConfig] = useState(null);
    const handleSortChange = useCallback((columnId, direction) => {
        if (!columnId || !direction) {
            setSortConfig(null); // clear sort
            return;
        }
        setSortConfig({ columnId, direction });
    }, []);
    const nameKey = useMemo(() => {
        const sample = tableData?.curr?.[0] || tableData?.prev?.[0];
        if (sample) {
            for (const key of NAME_KEYS) {
                if (sample[key] !== undefined)
                    return key;
            }
        }
        if (channelKey === "campaigns")
            return "campaign_name";
        if (channelKey === "flows")
            return "flow_name";
        if (channelKey === "sms")
            return "sms_name";
        return "name";
    }, [tableData, channelKey]);
    const nameLabel = useMemo(() => {
        if (channelKey === "campaigns")
            return "Campaign Name";
        if (channelKey === "flows")
            return "Flow Name";
        if (channelKey === "sms")
            return "SMS Name";
        return "Name";
    }, [channelKey]);
    const EXCLUDED_KEYS = ["message_id", "audiences"];
    const metrics = useMemo(() => {
        const sample = tableData?.curr?.[0] || tableData?.prev?.[0];
        if (!sample)
            return [];
        return Object.keys(sample).filter(k => !NAME_KEYS.includes(k) && !EXCLUDED_KEYS.includes(k));
    }, [tableData]);
    const rows = useMemo(() => {
        const dataSource = showPrevData ? tableData?.prev : tableData?.curr;
        const data = dataSource || [];
        return data.map((row, index) => {
            const isSummary = row?.[nameKey] === "All Total";
            // ✅ Best: message_id unique hota hai (campaigns/sms me)
            // ✅ Fallback: name + index
            const stableId = isSummary
                ? `${channelKey}-${showPrevData ? "prev" : "curr"}-summary`
                : `${channelKey}-${showPrevData ? "prev" : "curr"}-${row?.message_id || row?.[nameKey] || index}`;
            return {
                id: stableId,
                name: isSummary ? "Summary" : (row?.[nameKey] || `Row ${index + 1}`),
                data: row,
                isSummary
            };
        });
    }, [tableData, nameKey, showPrevData, channelKey]);
    const filteredRows = useMemo(() => {
        if (!searchQuery.trim())
            return rows;
        const query = searchQuery.toLowerCase().trim();
        return rows.filter(row => {
            if (row.isSummary)
                return true;
            return row.name?.toLowerCase().includes(query);
        });
    }, [rows, searchQuery]);
    const sortedRows = useMemo(() => {
        if (!sortConfig?.columnId || !sortConfig?.direction)
            return filteredRows;
        const { columnId, direction } = sortConfig;
        const summary = filteredRows.filter(r => r.isSummary);
        const normal = filteredRows.filter(r => !r.isSummary);
        const getValue = (row) => {
            if (columnId === "name")
                return row.name ?? "";
            return row.data?.[columnId];
        };
        const toComparable = (v) => {
            if (v === null || v === undefined)
                return null;
            // number-like strings
            if (typeof v === "string") {
                const n = Number(String(v).replace(/,/g, ""));
                if (!Number.isNaN(n))
                    return n;
                return v.toLowerCase();
            }
            return v;
        };
        normal.sort((a, b) => {
            const av = toComparable(getValue(a));
            const bv = toComparable(getValue(b));
            // nulls at end
            if (av === null && bv === null)
                return 0;
            if (av === null)
                return 1;
            if (bv === null)
                return -1;
            if (typeof av === "number" && typeof bv === "number") {
                return direction === "asc" ? av - bv : bv - av;
            }
            // string compare
            return direction === "asc"
                ? String(av).localeCompare(String(bv))
                : String(bv).localeCompare(String(av));
        });
        return [...normal, ...summary];
    }, [filteredRows, sortConfig]);
    const hiddenColumns = useMemo(() => {
        return getHiddenColumnsFromPreferences(preferences, networkKey);
    }, [preferences, networkKey]);
    const columnOrder = useMemo(() => {
        return getColumnOrderFromPreferences(preferences, networkKey, metrics);
    }, [preferences, networkKey, metrics]);
    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: "name",
                label: nameLabel,
                width: "280px",
                minWidth: "280px",
                draggable: false,
                sortable: true
            },
        ];
        // ✅ Add preview column only if NOT flows
        if (showPreviewColumn) {
            baseColumns.push({
                id: "preview",
                label: "Preview",
                width: "80px",
                minWidth: "80px",
                draggable: false,
                sortable: false
            });
        }
        const METRIC_LABEL_MAP = {
            place_rate: "Place Order Rate"
        };
        const titleCase = (text) => text
            .split(" ")
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(" ");
        const metricColumns = metrics.map(metric => ({
            id: metric,
            label: METRIC_LABEL_MAP[metric] || titleCase(metric.replace(/_/g, " ")),
            width: "200px",
            minWidth: "180px",
            draggable: true,
            sortable: true
        }));
        return [...baseColumns, ...metricColumns];
    }, [metrics, nameLabel, showPreviewColumn]);
    // ✅ Update handlePreviewClick
    const handlePreviewClick = useCallback((row) => {
        setSelectedRowForPreview({
            name: row.name,
            message_id: row.data?.message_id,
            audiences: row.data?.audiences,
        });
        setPreviewModalOpen(true);
    }, []);
    // ✅ Close handler
    const handlePreviewModalClose = useCallback(() => {
        setPreviewModalOpen(false);
        setSelectedRowForPreview(null);
    }, []);
    const renderCell = (row, column) => {
        if (column.id === "name") {
            return (_jsx(HoverTooltip, { title: row.name, placement: "top-start", children: _jsx(Typography, { fontWeight: row.isSummary ? 700 : 400, className: row.isSummary ? "all-Font-Sizes-16" : "all-Font-Sizes", sx: {
                        fontSize: '14px !important',
                        color: '#181D27',
                        maxWidth: '250px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        cursor: 'pointer',
                    }, children: row.name }) }));
        }
        if (column.id === "preview") {
            if (row.isSummary) {
                return null;
            }
            return (_jsx(IconButton, { onClick: () => handlePreviewClick(row), sx: {
                    padding: '6px',
                    '&:hover': {
                        backgroundColor: '#F4F4F5',
                    }
                }, children: _jsx("img", { src: "/assets/eye.svg", alt: "Preview", style: {
                        width: '20px',
                        height: '20px',
                    } }) }));
        }
        const value = row.data?.[column.id];
        // send_date arrives as YYYY-MM-DD (so sorting is chronological) — show it as "Oct 01, 2026".
        // Built from the parts, not new Date(string), so the day can't shift with the browser's timezone.
        if (column.id === "send_date" && typeof value === "string") {
            const [y, m, d] = value.split("-").map(Number);
            if (y && m && d) {
                const text = new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
                return (_jsx(Typography, { className: row.isSummary ? "all-Font-Sizes-16" : "all-Font-Sizes", sx: { fontSize: '14px !important', color: '#181D27', fontWeight: row.isSummary ? 700 : 400 }, children: text }));
            }
        }
        return (_jsx(StyledCurrency, { className: row.isSummary ? "all-Font-Sizes-16" : "all-Font-Sizes", fontSize: "14px", valueWeight: row.isSummary ? 700 : 400, symbolWeight: row.isSummary ? 700 : 400, valueColor: "#181D27", symbolColor: "#98a2b3", value: value !== undefined && value !== null ? formatStat(value, column.id) : '-', style: { display: 'inline-block' } }));
    };
    const handleColumnVisibilityChange = useCallback((newHidden) => {
        const existingPrefs = preferences?.[networkKey] || [];
        const existingOrderMap = new Map();
        existingPrefs.forEach((item, index) => {
            const key = Object.keys(item)[0];
            existingOrderMap.set(key, item[key]?.order ?? index + 1);
        });
        const newPreferences = [];
        metrics.forEach((metric, index) => {
            const isHidden = newHidden.has(metric);
            const existingOrder = existingOrderMap.get(metric) ?? index + 1;
            newPreferences.push({
                [metric]: {
                    visibility: !isHidden,
                    order: existingOrder
                }
            });
        });
        dispatch(updateNetworkPreference({ networkKey, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [networkKey]: newPreferences
        };
        dispatch(savePreferences({ preferences: updatedPreferences }));
    }, [dispatch, metrics, networkKey, preferences]);
    const handleColumnOrderChange = useCallback((newOrder) => {
        const existingPrefs = preferences?.[networkKey] || [];
        const existingVisibilityMap = new Map();
        existingPrefs.forEach(item => {
            const key = Object.keys(item)[0];
            existingVisibilityMap.set(key, item[key]?.visibility ?? true);
        });
        const newPreferences = [];
        newOrder.forEach((metric, index) => {
            if (metric === "name" || metric === "preview")
                return;
            const isVisible = existingVisibilityMap.get(metric) ?? true;
            newPreferences.push({
                [metric]: { visibility: isVisible, order: index + 1 }
            });
        });
        metrics.forEach((metric, index) => {
            if (!newOrder.includes(metric)) {
                const isVisible = existingVisibilityMap.get(metric) ?? true;
                newPreferences.push({
                    [metric]: { visibility: isVisible, order: newOrder.length + index + 1 }
                });
            }
        });
        dispatch(updateNetworkPreference({ networkKey, metrics: newPreferences }));
        const updatedPreferences = {
            ...preferences,
            [networkKey]: newPreferences
        };
        dispatch(savePreferences({ preferences: updatedPreferences }));
    }, [dispatch, metrics, networkKey, preferences]);
    const handleSearch = useCallback((query) => {
        setSearchQuery(query);
    }, []);
    const handleExport = useCallback(() => {
        const activeDateRange = showPrevData ? previousDateRange : currentDateRange;
        let startDate = '';
        let endDate = '';
        if (activeDateRange) {
            startDate = calendarDateToString(activeDateRange.start);
            endDate = calendarDateToString(activeDateRange.end);
        }
        exportEmailChannelExcel(title, networkKey, metrics, filteredRows, {
            preferences: preferences,
            nameKey: nameKey,
            nameLabel: nameLabel,
            startDate: startDate,
            endDate: endDate,
        });
    }, [
        title, networkKey, metrics, filteredRows,
        preferences, nameKey, nameLabel,
        showPrevData, currentDateRange, previousDateRange
    ]);
    const NAME_COL_WIDTH = 300;
    const PREVIEW_COL_WIDTH = 80;
    // ✅ Calculate left position for columns after name
    const secondColumnLeft = showPreviewColumn ? `${NAME_COL_WIDTH}px` : undefined;
    return (_jsxs(Box, { sx: {
            border: "1px solid #D0D5DD",
            borderRadius: "8px",
            mb: 2,
            overflow: "hidden",
            // ✅ Hide scrollbar
            '& .MuiTableContainer-root::-webkit-scrollbar': {
                display: 'none',
            },
            '& .MuiTableContainer-root': {
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
            },
            // ✅ Table layout
            '& .MuiTable-root': {
                tableLayout: 'fixed',
            },
            // All cells minimum width
            '& .MuiTableCell-root': {
                minWidth: '180px !important',
                whiteSpace: 'nowrap',
                padding: '8px !important',
                borderRight: '1px solid #EAECF0',
            },
            '& .MuiTableRow-root .MuiTableCell-root:last-child': {
                borderRight: 'none',
            },
            // ✅ FIRST COLUMN (Name) - STICKY
            '& .MuiTable-root .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:nth-of-type(1)': {
                position: 'sticky',
                left: 0,
                backgroundColor: '#fff',
                zIndex: 3,
                minWidth: `${NAME_COL_WIDTH}px !important`,
                width: `${NAME_COL_WIDTH}px !important`,
                // ✅ Add border if no preview column
                ...(showPreviewColumn ? {} : { borderRight: "1px solid #E4E7EC" }),
            },
            '& .MuiTable-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:nth-of-type(1)': {
                position: 'sticky',
                left: 0,
                backgroundColor: '#F9FAFB',
                zIndex: 6,
                minWidth: `${NAME_COL_WIDTH}px !important`,
                width: `${NAME_COL_WIDTH}px !important`,
                ...(showPreviewColumn ? {} : { borderRight: "1px solid #E4E7EC" }),
            },
            // ✅ SECOND COLUMN (Preview) - Only if showPreviewColumn
            ...(showPreviewColumn ? {
                '& .MuiTable-root .MuiTableBody-root .MuiTableRow-root .MuiTableCell-root:nth-of-type(2)': {
                    position: 'sticky',
                    left: `300px !important`,
                    backgroundColor: '#fff',
                    zIndex: 2,
                    minWidth: `${PREVIEW_COL_WIDTH}px !important`,
                    width: `${PREVIEW_COL_WIDTH}px !important`,
                    borderRight: "1px solid #E4E7EC",
                },
                '& .MuiTable-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root:nth-of-type(2)': {
                    position: 'sticky',
                    left: `300px !important`,
                    backgroundColor: '#F9FAFB',
                    zIndex: 5,
                    minWidth: `${PREVIEW_COL_WIDTH}px !important`,
                    width: `${PREVIEW_COL_WIDTH}px !important`,
                    borderRight: "1px solid #E4E7EC",
                },
            } : {}),
            // ✅ Header cells padding
            '& .MuiTableHead-root .MuiTableCell-root': {
                padding: '8px !important',
            },
            // ✅ Last column content align to start
            '& .MuiTableBody-root tr td:nth-last-child(1) .user-table-actions-cell': {
                justifyContent: 'flex-start !important',
            },
            '& .MuiTableHead-root tr th:nth-last-child(1) .MuiBox-root': {
                justifyContent: 'flex-start !important',
            },
        }, children: [_jsxs(Box, { sx: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    p: 1,
                    borderBottom: "1px solid #EAECF0",
                    backgroundColor: "#fff",
                }, className: "email-channel", children: [_jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, children: [_jsx(Typography, { fontWeight: 600, className: "all-Font-Sizes-16", children: title }), tableData?.prev && (_jsxs(_Fragment, { children: [_jsx(Box, { sx: {
                                            width: '1px',
                                            height: '20px',
                                            backgroundColor: '#D0D5DD'
                                        } }), _jsx(Box, { className: "compare-toggle-container", children: _jsx(FormControlLabel, { control: _jsx(Switch, { checked: showPrevData, onChange: (e) => setShowPrevData(e.target.checked), className: "compare-switch" }), label: "View Compare Data Records", className: "compare-label all-Font-Sizes", sx: {
                                                '.MuiTypography-root': { fontWeight: 500, fontSize: '13px !important' },
                                                margin: 0
                                            } }) })] }))] }), _jsxs(Box, { sx: { display: "flex", alignItems: "center", gap: 1 }, className: "email-table-toolbar  small-icon", children: [_jsx(TableToolbar, { showSearch: true, searchValue: searchQuery, onSearchChange: handleSearch, title: "", enableColumnVisibility: true, onColumnVisibilityClick: (e) => setColumnVisibilityAnchor(e.currentTarget) }), _jsx(ExportButton, { onClick: handleExport })] })] }), _jsx(CommonTable, { columns: columns, type: "email-channel-table", rows: sortedRows, renderCell: renderCell, sortConfig: sortConfig, onSortChange: handleSortChange, stickyHeader: true, maxHeight: "500px", expandTrigger: "none", hiddenColumns: hiddenColumns, onColumnVisibilityChange: handleColumnVisibilityChange, columnVisibilityAnchorEl: columnVisibilityAnchor, onColumnVisibilityClose: () => setColumnVisibilityAnchor(null), columnVisibilityConfig: {
                    enable: true,
                    columns: metrics,
                    enableReorder: true,
                    showHiddenColumns: true
                }, enableColumnReorder: true, columnOrder: columnOrder, onColumnOrderChange: handleColumnOrderChange, pinnedColumns: {
                    left: showPreviewColumn ? ["name", "preview"] : ["name"],
                    right: []
                }, rowSx: (row) => {
                    if (row.isSummary) {
                        return {
                            "& .MuiTableCell-root": {
                                position: "sticky",
                                bottom: 0,
                                backgroundColor: "#F4F4F5 !important",
                                fontWeight: 600,
                                zIndex: 3,
                                borderTop: '1px solid #e2e8f0',
                            },
                            // ✅ Name column in summary
                            "& .MuiTableCell-root:nth-of-type(1)": {
                                zIndex: 7,
                                left: 0,
                                backgroundColor: "#F4F4F5 !important",
                            },
                            // ✅ Preview column in summary - only if showPreviewColumn
                            ...(showPreviewColumn ? {
                                "& .MuiTableCell-root:nth-of-type(2)": {
                                    zIndex: 6,
                                    left: `${NAME_COL_WIDTH}px`,
                                    backgroundColor: "#F4F4F5 !important",
                                }
                            } : {})
                        };
                    }
                    return {};
                } }, `${channelKey}-${showPrevData ? "prev" : "curr"}`), showPreviewColumn && (_jsx(CampaignPreviewDrawer, { open: previewModalOpen, onClose: handlePreviewModalClose, rowData: selectedRowForPreview, channelKey: channelKey, clientId: clientId }))] }));
};
export default EmailChannelTable;
